import { type V2, TAU, angleTo } from '../../core/math';
import type { Actor } from '../../sim/actor';
import { type BossScript, registerBossScript } from '../../sim/ai/brains';
import { createMonster } from '../../sim/factory';
import { registerStatus } from '../../sim/status';
import type { World } from '../../sim/world';
import { MIRROR } from '../monsters/keep';

/**
 * Boss scripts for the story dungeon bosses, the named field bosses and the
 * Rift Delve floor bosses (monster data lives in src/content/monsters/*).
 *
 * Multiplayer rules: scripts run only on the host (brains run for locally
 * owned monsters). Every damaging mechanic is a skill cast via
 * `world.monsterCast` (casts replicate; hits on players resolve on each
 * player's own peer), helpers are spawned with `createMonster`, statuses go
 * through `world.grantStatus` / `world.revokeStatus`, and randomness uses
 * `world.rng`.
 */

registerStatus({
  id: 'warden_shell', name: 'Sand Shell', kind: 'buff', icon: 'warden_shell',
  description: 'Hardened sand: damage taken -50% until the shell is broken.',
  stacking: 'refresh', mods: { dmgReduction: 0.5 }, color: 0xd6b27a,
});

/** Maximum living adds per boss. */
export const ADD_CAPS = { grubling_chieftain: 5, rimefang_alpha: 4, voidmaw: 4, rift_warden: 6 } as const;

// ---------------------------------------------------------------------------
// Script helpers (host only).

interface StoryState {
  /** Ids of summoned adds (pruned when they die). */
  adds: number[];
  /** Cast id already handled per skill, for once-per-cast triggers. */
  handled: Record<string, number>;
  /** Skills cast as soon as the boss is free (mechanic follow-ups). */
  queue: string[];
  flags: Record<string, boolean>;
  /** Reflections: id of the boss they mirror. */
  master: number;
}

function story(a: Actor): StoryState {
  const brain = a.monster!.brain;
  let s = brain.story as StoryState | undefined;
  if (!s) {
    s = { adds: [], handled: {}, queue: [], flags: {}, master: 0 };
    brain.story = s;
  }
  return s;
}

/** Where the boss's current target stands (or a point ahead of the boss). */
function targetPos(world: World, boss: Actor): V2 {
  const id = boss.monster?.targetId;
  const t = id != null ? world.actors.get(id) : undefined;
  if (t?.alive) return { ...t.pos };
  const near = world.nearestEnemy(boss, 40);
  if (near) return { ...near.pos };
  return { x: boss.pos.x + Math.sin(boss.facing) * 3, z: boss.pos.z + Math.cos(boss.facing) * 3 };
}

/** Casts a mechanic skill now, ignoring its cooldown. Returns false if the boss cannot act. */
export function forceCast(world: World, boss: Actor, skillId: string, point?: V2): boolean {
  boss.cooldowns.delete(skillId);
  const p = point ?? targetPos(world, boss);
  world.faceTowards(boss, p);
  return world.monsterCast(boss, skillId, p) !== null;
}

/** Runs `fn` once per cast when the boss's cast of `skillId` passes `at` seconds. */
function atCastTime(boss: Actor, skillId: string, at: number, fn: () => void): void {
  const c = boss.cast;
  if (!c || c.skill.id !== skillId || c.phase !== 'stage' || c.t < at) return;
  const s = story(boss);
  if (s.handled[skillId] === c.castId) return;
  s.handled[skillId] = c.castId;
  fn();
}

/** Living adds of a boss. */
export function livingAdds(world: World, boss: Actor): number[] {
  const s = story(boss);
  s.adds = s.adds.filter((id) => world.actors.get(id)?.alive);
  return s.adds;
}

/** Spawns up to `count` adds around the boss without exceeding `cap` living adds. */
export function spawnAdds(world: World, boss: Actor, defId: string, count: number, cap: number, radius = 5): Actor[] {
  const adds = livingAdds(world, boss);
  const n = Math.max(0, Math.min(count, cap - adds.length));
  const m = boss.monster!;
  const out: Actor[] = [];
  for (let i = 0; i < n; i++) {
    const ang = world.rng.range(0, TAU);
    let p: V2 = { x: boss.pos.x + Math.sin(ang) * radius, z: boss.pos.z + Math.cos(ang) * radius };
    if (!world.nav.walkable(p)) p = world.nav.nearestWalkable(p) ?? { ...boss.pos };
    const add = createMonster(world, defId, m.level, p, { facing: angleTo(boss.pos, p), tags: ['summoned'] });
    const am = add.monster!;
    am.aggroRange = Math.max(am.aggroRange, 30);
    am.leash = Math.max(am.leash, 40);
    if (m.targetId !== null) am.threat.set(m.targetId, 1);
    adds.push(add.id);
    out.push(add);
  }
  return out;
}

/** Keeps a summon skill off the pattern rotation while the add cap is reached. */
function holdWhileCapped(world: World, boss: Actor, skillId: string, cap: number): void {
  if (livingAdds(world, boss).length >= cap) boss.cooldowns.set(skillId, Math.max(boss.cooldowns.get(skillId) ?? 0, 1));
}

function pendingAreas(world: World, a: Actor): boolean {
  const c = a.cast;
  return !!c && world.areas.some((ar) => !ar.dead && ar.ownerId === a.id && ar.castId === c.castId);
}

/**
 * Ends a long channel once its pending areas have resolved: mechanic stages
 * are padded so they outlast their telegraphs even at the attack-speed cap,
 * and this trims the padding.
 */
function endWhenResolved(world: World, boss: Actor, skillId: string, minT: number): void {
  const c = boss.cast;
  if (!c || c.skill.id !== skillId || c.t < minT || pendingAreas(world, boss)) return;
  world.endCast(boss, false);
}

/** Casts queued follow-ups as soon as the boss is free. */
function drainQueue(world: World, boss: Actor): void {
  const s = story(boss);
  if (boss.cast || s.queue.length === 0) return;
  const next = s.queue.shift();
  if (next) forceCast(world, boss, next);
}

/** Stagger check: refills the stagger bar, then channels `skillId` (a lethal blast unless the bar is emptied in time). */
function staggerCheck(skillId: string): (world: World, boss: Actor) => void {
  return (world, boss) => {
    const st = boss.monster?.stagger;
    if (st) {
      st.active = true;
      st.value = st.max;
      st.recoverTimer = 0;
    }
    forceCast(world, boss, skillId, { ...boss.pos });
  };
}

const cast = (skillId: string, onSelf = false) => (world: World, boss: Actor): void => {
  forceCast(world, boss, skillId, onSelf ? { ...boss.pos } : undefined);
};

// ---------------------------------------------------------------------------
// Act I — Grubling Chieftain: club slams, leap, counterable charge, summons,
// stagger check at 50%, frenzy at 25%.

const chieftain: BossScript = {
  patterns: [
    { skill: 'chief_club_slam', weight: 3, range: [0, 3.5] },
    { skill: 'chief_twin_smash', weight: 2, range: [0, 3.5] },
    { skill: 'chief_brutal_swipe', weight: 2, range: [0, 4] },
    { skill: 'chief_crushing_leap', weight: 2, range: [4, 14], target: 'far' },
    { skill: 'chief_shardbound_charge', weight: 1.5, range: [3, 13] },
    { skill: 'chief_rally', weight: 1, phases: [2, 3] },
    { skill: 'chief_shard_fissure', weight: 2, range: [0, 12], phases: [2, 3] },
    { skill: 'chief_warren_quake', weight: 1.5, range: [0, 6], phases: [2, 3] },
  ],
  phaseAt: [60, 25],
  mechanics: [
    { atPct: 75, name: 'call_the_warren', run: cast('chief_rally') },
    { atPct: 50, name: 'shards_fury', run: staggerCheck('chief_shard_fury') },
    {
      atPct: 25, name: 'frenzy',
      run: (world, boss) => {
        forceCast(world, boss, 'chief_frenzy_roar', { ...boss.pos });
        world.grantStatus(boss, { id: 'atkUp', duration: 1e9, value: 0.25 }, boss.id);
      },
    },
  ],
  enrageAfter: 480,
  tick: (world, boss) => {
    const cap = ADD_CAPS.grubling_chieftain;
    atCastTime(boss, 'chief_rally', 0.8, () => spawnAdds(world, boss, 'grubling', 3, cap));
    atCastTime(boss, 'chief_frenzy_roar', 0.9, () => {
      spawnAdds(world, boss, 'grubling_shaman', 1, cap);
      spawnAdds(world, boss, 'grubling', 2, cap);
    });
    holdWhileCapped(world, boss, 'chief_rally', cap);
    endWhenResolved(world, boss, 'chief_shard_fury', 0.5);
  },
};

// ---------------------------------------------------------------------------
// Act II — Sandbound Warden: Sand Shell destruction (-50% damage taken until the
// part breaks), sandstorm donut, rolling boulders, counter windows, collapses.

const warden: BossScript = {
  patterns: [
    { skill: 'warden_cleave', weight: 3, range: [0, 5] },
    { skill: 'warden_pillar_slam', weight: 2.5, range: [0, 5.5] },
    { skill: 'warden_judgement', weight: 1.5, range: [0, 8] },
    { skill: 'warden_rush', weight: 1.5, range: [4, 14] },
    { skill: 'warden_boulders', weight: 1.5, range: [0, 20] },
    { skill: 'warden_quicksand', weight: 1.5, range: [3, 18], target: 'far' },
    { skill: 'warden_sandstorm', weight: 1.2, range: [0, 10], phases: [2, 3] },
  ],
  phaseAt: [60, 30],
  mechanics: [
    { atPct: 80, name: 'sandstorm_veil', run: cast('warden_sandstorm', true) },
    { atPct: 50, name: 'tomb_collapse', run: cast('warden_collapse', true) },
    {
      atPct: 20, name: 'tomb_collapse_2',
      run: (world, boss) => {
        forceCast(world, boss, 'warden_collapse', { ...boss.pos });
        story(boss).queue.push('warden_boulders');
      },
    },
  ],
  enrageAfter: 540,
  tick: (world, boss) => {
    const s = story(boss);
    const shell = boss.monster!.parts.find((p) => p.id === 'sand_shell');
    if (!s.flags.shell) {
      s.flags.shell = true;
      if (shell && !shell.broken) world.grantStatus(boss, { id: 'warden_shell', duration: 1e9 }, boss.id);
    }
    if (shell?.broken && boss.hasStatus('warden_shell')) {
      world.revokeStatus(boss, ['warden_shell'], boss.id);
      forceCast(world, boss, 'warden_shatter', { ...boss.pos });
      return;
    }
    if (!s.flags.awake && boss.monster!.targetId !== null && !boss.cast) {
      s.flags.awake = true;
      forceCast(world, boss, 'warden_awaken', { ...boss.pos });
      return;
    }
    drainQueue(world, boss);
  },
};

// ---------------------------------------------------------------------------
// Act III — Frost Matriarch: ice lance volleys, blizzard donut, counter windows,
// Hall of Mirrors (only the real one can be countered), enrage below 25%.

const MIRROR_RADIUS = 8.5;

function hallOfMirrors(world: World, boss: Actor): void {
  const m = boss.monster!;
  const center = { ...m.home };
  const base = world.rng.range(0, TAU);
  const spots = [0, 1, 2].map((i): V2 => {
    const ang = base + (i * TAU) / 3;
    const p = { x: center.x + Math.sin(ang) * MIRROR_RADIUS, z: center.z + Math.cos(ang) * MIRROR_RADIUS };
    return world.nav.walkable(p) ? p : (world.nav.nearestWalkable(p) ?? { ...center });
  });
  const real = world.rng.int(0, 2);
  // She blinks to her spot (a host-side move that replicates like any other);
  // her areas spawn MIRROR.spawnAt seconds later so peers see the new position.
  const spot = spots[real] as V2;
  boss.pos = { ...spot };
  boss.prevPos = { ...spot };
  boss.path = [];
  boss.displacement = null;
  if (!forceCast(world, boss, 'mat_mirror', center)) return;
  spots.forEach((p, i) => {
    if (i === real) return;
    const r = createMonster(world, 'frozen_reflection', m.level, p, { facing: angleTo(p, center), tags: ['summoned', 'illusion'] });
    story(r).master = boss.id;
    world.monsterCast(r, 'refl_mirror', center);
  });
}

const matriarch: BossScript = {
  patterns: [
    { skill: 'mat_lance_volley', weight: 3, range: [0, 20] },
    { skill: 'mat_piercing_lance', weight: 2, range: [3, 25], target: 'far' },
    { skill: 'mat_frozen_spires', weight: 2, range: [0, 15] },
    { skill: 'mat_frost_nova', weight: 2, range: [0, 4.5] },
    { skill: 'mat_frost_step', weight: 1.5, range: [0, 4] },
    { skill: 'mat_glacial_judgement', weight: 1.5, range: [0, 7] },
    { skill: 'mat_blizzard_ring', weight: 1.5, range: [0, 12], phases: [2, 3] },
  ],
  phaseAt: [60, 25],
  mechanics: [
    { atPct: 70, name: 'hall_of_mirrors', run: hallOfMirrors },
    { atPct: 40, name: 'hall_of_mirrors_2', run: hallOfMirrors },
    { atPct: 25, name: 'winters_wrath', run: cast('mat_fury', true) },
  ],
  enrageAfter: 600,
  onPhase: (world, boss, phase) => {
    if (phase >= 3) world.grantStatus(boss, { id: 'enraged', duration: 1e9 }, boss.id);
  },
  tick: (world, boss) => endWhenResolved(world, boss, 'mat_mirror', MIRROR.spawnAt + 0.1),
};

/**
 * Frozen reflections cast a look-alike requiem without a counter window. They
 * shatter (cancelling their pending nova) when the true Matriarch is countered,
 * staggered or killed, and linger no longer than their own cast.
 */
const reflection: BossScript = {
  patterns: [],
  phaseAt: [],
  mechanics: [],
  enrageAfter: 0,
  tick: (world, r) => {
    const master = world.actors.get(story(r).master);
    const broken = !master || !master.alive || (master.monster?.countered ?? 0) > 0 || master.hasStatus('staggered');
    const done = !r.cast || (r.cast.t > MIRROR.spawnAt + 0.1 && !pendingAreas(world, r));
    if (broken || done) world.kill(r, null);
  },
};

// ---------------------------------------------------------------------------
// Field bosses.

/** Dunemaw (Sunscar): burrow-and-emerge, tail sweeps, poison pools. */
const dunemaw: BossScript = {
  patterns: [
    { skill: 'dunemaw_pincers', weight: 3, range: [0, 4] },
    { skill: 'dunemaw_tail_sweep', weight: 2, range: [0, 6] },
    { skill: 'dunemaw_stinger', weight: 2, range: [0, 7] },
    { skill: 'dunemaw_burrow', weight: 1.5, target: 'random' },
    { skill: 'dunemaw_venom_rain', weight: 2, range: [3, 16], target: 'far' },
  ],
  phaseAt: [50],
  mechanics: [
    { atPct: 60, name: 'venom_tide', run: cast('dunemaw_venom_tide', true) },
    {
      atPct: 30, name: 'frenzied_burrow',
      run: (world, boss) => {
        forceCast(world, boss, 'dunemaw_frenzy_burrow');
        story(boss).queue.push('dunemaw_burrow', 'dunemaw_burrow');
      },
    },
  ],
  enrageAfter: 0,
  tick: (world, boss) => drainQueue(world, boss),
};

/** Rimefang Alpha (Frosthollow): pack howls summon frost wolves, frost breath, Winter's Maw. */
const rimefang: BossScript = {
  patterns: [
    { skill: 'rime_maul', weight: 3, range: [0, 4.5] },
    { skill: 'rime_pounce', weight: 2, range: [4, 14], target: 'far' },
    { skill: 'rime_breath', weight: 2, range: [0, 9] },
    { skill: 'rime_tail', weight: 1.5, range: [0, 5] },
    { skill: 'rime_howl', weight: 1, phases: [2] },
  ],
  phaseAt: [50],
  mechanics: [
    { atPct: 70, name: 'call_the_pack', run: cast('rime_howl') },
    { atPct: 35, name: 'winters_maw', run: cast('rime_winters_maw', true) },
  ],
  enrageAfter: 0,
  tick: (world, boss) => {
    const cap = ADD_CAPS.rimefang_alpha;
    atCastTime(boss, 'rime_howl', 0.9, () => spawnAdds(world, boss, 'frost_wolf', 3, cap, 6));
    holdWhileCapped(world, boss, 'rime_howl', cap);
  },
};

// ---------------------------------------------------------------------------
// Rift Delve floor bosses (compact).

const riftWarden: BossScript = {
  patterns: [
    { skill: 'rwarden_slam', weight: 3, range: [0, 4.5] },
    { skill: 'rwarden_spikes', weight: 2, range: [0, 12] },
    { skill: 'rwarden_barrage', weight: 2, range: [0, 16] },
  ],
  phaseAt: [50],
  mechanics: [{ atPct: 50, name: 'rift_collapse', run: cast('rwarden_collapse', true) }],
  enrageAfter: 0,
  // The collapse tears the rift open: imps pour out.
  tick: (world, boss) => atCastTime(boss, 'rwarden_collapse', 1.2, () => spawnAdds(world, boss, 'rift_imp', 4, ADD_CAPS.rift_warden, 7)),
};

const voidmaw: BossScript = {
  patterns: [
    { skill: 'voidmaw_rend', weight: 3, range: [0, 4.5] },
    { skill: 'voidmaw_pounce', weight: 2, range: [4, 15], target: 'far' },
    { skill: 'voidmaw_breath', weight: 2, range: [0, 8] },
    { skill: 'voidmaw_devour', weight: 1.2, range: [0, 9], phases: [2] },
  ],
  phaseAt: [50],
  mechanics: [
    { atPct: 70, name: 'hungering_howl', run: cast('voidmaw_howl') },
    { atPct: 35, name: 'hungering_howl_2', run: cast('voidmaw_howl') },
  ],
  enrageAfter: 0,
  tick: (world, boss) => atCastTime(boss, 'voidmaw_howl', 0.9, () => spawnAdds(world, boss, 'rift_hound', 3, ADD_CAPS.voidmaw, 6)),
};

/** Every script by monster def id (the tests check them against the monster data). */
export const STORY_BOSS_SCRIPTS: Readonly<Record<string, BossScript>> = {
  grubling_chieftain: chieftain,
  sandbound_warden: warden,
  frost_matriarch: matriarch,
  frozen_reflection: reflection,
  dunemaw,
  rimefang_alpha: rimefang,
  rift_warden: riftWarden,
  voidmaw,
};

/** Registers the story, field and rift boss scripts. Safe to call more than once. */
export function registerStoryBosses(): void {
  for (const [id, script] of Object.entries(STORY_BOSS_SCRIPTS)) registerBossScript(id, script);
}
